import { useCallback, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Field } from '../components/Field';
import { Spinner } from '../components/Spinner';
import { formatDateTime } from '../lib/dates';
import { errorMessage } from '../lib/errors';
import { useLoaded } from '../lib/hooks';
import {
  addStaff,
  deleteStaff,
  listStaff,
  passwordLink,
  resetTwoStep,
  updateStaff,
  validateNewStaff,
  STAFF_NAME_MAX,
  type StaffChanges,
  type StaffInput,
  type StaffMember,
  type StaffRole,
} from '../lib/staff';
import type { FieldErrors } from '../lib/validation';

const ROLE_LABELS: Record<StaffRole, string> = { staff: 'Staff', admin: 'Admin' };

interface Notice {
  text: string;
  /** A password-setup link to hand over, shown with a copy button. */
  link?: string;
}

/** Admins manage who can sign in and with which role. Everything goes through the staff API. */
export function StaffPage() {
  const { state } = useAuth();
  const myUid = state.status === 'signedIn' ? state.user.uid : '';
  const { loading, value: staff, error, reload } = useLoaded('staff', listStaff);
  const [adding, setAdding] = useState(false);
  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [actionError, setActionError] = useState('');

  /** Runs one action from a row or the edit panel, then refreshes the list. */
  const act = useCallback(
    async (action: () => Promise<Notice | void>) => {
      setActionError('');
      setNotice(null);
      try {
        const result = await action();
        if (result) setNotice(result);
        reload();
      } catch (err) {
        setActionError(errorMessage(err));
      }
    },
    [reload],
  );

  function remove(m: StaffMember) {
    if (!window.confirm(`Delete the account ${m.email}? They will no longer be able to sign in.`)) return;
    void act(async () => {
      await deleteStaff(m.uid);
      setEditingUid(null);
      return { text: `Deleted ${m.email}.` };
    });
  }

  return (
    <section>
      <div className="page-head">
        <h1>Staff</h1>
        {!adding && (
          <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
            Add staff member
          </button>
        )}
      </div>

      {(error || actionError) && (
        <p className="alert alert--error" role="alert">
          {error || actionError}
        </p>
      )}
      {notice && <NoticeBox notice={notice} />}

      {adding && (
        <AddForm
          onCancel={() => setAdding(false)}
          onAdded={(m) => {
            setAdding(false);
            setActionError('');
            setNotice({
              text: `Created ${m.email} as ${m.role ?? 'staff'}. Ask them to open the app and choose "Forgot password?" to set a password, or use Edit to get a setup link.`,
            });
            reload();
          }}
        />
      )}

      {loading ? (
        <Spinner />
      ) : !staff || staff.length === 0 ? (
        !error && <p className="empty">No accounts yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Name</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Two-step</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {staff.map((m) =>
                editingUid === m.uid ? (
                  <tr key={m.uid}>
                    <td colSpan={7}>
                      <EditPanel
                        member={m}
                        isSelf={m.uid === myUid}
                        act={act}
                        onClose={() => setEditingUid(null)}
                        onDelete={() => remove(m)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={m.uid}>
                    <td>
                      {m.email}
                      {m.uid === myUid && <span className="tag">you</span>}
                    </td>
                    <td>{m.name}</td>
                    <td>{m.role ? ROLE_LABELS[m.role] : <span className="muted">No access</span>}</td>
                    <td>{m.disabled ? 'Disabled' : 'Active'}</td>
                    <td>{m.twoStep ? 'On' : <span className="muted">Off</span>}</td>
                    <td>{m.lastSignIn ? formatDateTime(new Date(m.lastSignIn)) : <span className="muted">Never</span>}</td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn--small"
                        onClick={() => {
                          setEditingUid(m.uid);
                          setNotice(null);
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function NoticeBox({ notice }: { notice: Notice }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="alert alert--success" role="status">
      <p>{notice.text}</p>
      {notice.link && (
        <>
          <p>
            <code className="secret">{notice.link}</code>
          </p>
          <button
            type="button"
            className="btn btn--small"
            onClick={() => {
              void navigator.clipboard.writeText(notice.link ?? '').then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </>
      )}
    </div>
  );
}

function RoleSelect(props: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  allowNone?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
}) {
  const { id, value, onChange, allowNone, disabled, error, hint } = props;
  const note = error ?? hint;
  return (
    <div className={error ? 'field field--error' : 'field'}>
      <label htmlFor={id}>Role</label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={note ? `${id}-note` : undefined}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="staff">Staff: clients and vehicles</option>
        <option value="admin">Admin: also manages staff accounts</option>
        {allowNone && <option value="">No access</option>}
      </select>
      {note && (
        <small id={`${id}-note`} className={error ? 'field__error' : 'field__hint'}>
          {note}
        </small>
      )}
    </div>
  );
}

function AddForm({ onAdded, onCancel }: { onAdded: (m: StaffMember) => void; onCancel: () => void }) {
  const [values, setValues] = useState<StaffInput>({ email: '', name: '', role: 'staff' });
  const [errors, setErrors] = useState<FieldErrors<StaffInput>>({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    const result = validateNewStaff(values);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      onAdded(await addStaff(result.value));
    } catch (err) {
      setFormError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form className="card form-grid" onSubmit={submit} noValidate>
      <Field
        label="Work email"
        name="email"
        type="email"
        autoComplete="off"
        value={values.email}
        onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
        error={errors.email}
        hint="They set their own password through an emailed link."
      />
      <Field
        label="Name (optional)"
        name="name"
        autoComplete="off"
        maxLength={STAFF_NAME_MAX}
        value={values.name}
        onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
        error={errors.name}
      />
      <RoleSelect
        id="f-role"
        value={values.role}
        onChange={(role) => setValues((v) => ({ ...v, role: role as StaffInput['role'] }))}
        error={errors.role}
      />
      {formError && (
        <p className="alert alert--error form-grid__full" role="alert">
          {formError}
        </p>
      )}
      <div className="actions form-grid__full">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Adding…' : 'Add staff member'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

interface EditPanelProps {
  member: StaffMember;
  isSelf: boolean;
  act: (action: () => Promise<Notice | void>) => Promise<void>;
  onClose: () => void;
  onDelete: () => void;
}

function EditPanel({ member, isSelf, act, onClose, onDelete }: EditPanelProps) {
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState<string>(member.role ?? '');
  const [busy, setBusy] = useState(false);
  const idPrefix = `s-${member.uid}`;

  async function run(action: () => Promise<Notice | void>) {
    setBusy(true);
    await act(action);
    setBusy(false);
  }

  function save(e: FormEvent) {
    e.preventDefault();
    const changes: StaffChanges = {};
    if (name.trim() !== member.name) changes.name = name;
    if (role !== (member.role ?? '')) changes.role = role === '' ? null : (role as StaffRole);
    void run(async () => {
      if (Object.keys(changes).length > 0) await updateStaff(member.uid, changes);
      onClose();
      return { text: `Saved ${member.email}.` };
    });
  }

  return (
    <form className="card form-grid" onSubmit={save} noValidate>
      <Field label="Email" name="email" id={`${idPrefix}-email`} value={member.email} readOnly disabled />
      <Field
        label="Name"
        name="name"
        id={`${idPrefix}-name`}
        autoComplete="off"
        maxLength={STAFF_NAME_MAX}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <RoleSelect
        id={`${idPrefix}-role`}
        value={role}
        onChange={setRole}
        allowNone
        disabled={isSelf}
        hint={isSelf ? 'You cannot change your own role. Ask another admin.' : 'A changed role applies within an hour, or at their next sign-in.'}
      />

      <div className="actions form-grid__full">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>

      <div className="actions form-grid__full">
        <button
          type="button"
          className="btn btn--small"
          disabled={busy}
          onClick={() =>
            void run(async () => ({
              text: `Password link for ${member.email}. It works once and expires in 1 hour; share it privately.`,
              link: await passwordLink(member.uid),
            }))
          }
        >
          Get password link
        </button>
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || !member.twoStep}
          title={member.twoStep ? undefined : 'Two-step verification is not set up'}
          onClick={() => {
            if (!window.confirm(`Remove two-step verification from ${member.email}? Use this when they lost their phone.`)) return;
            void run(async () => {
              await resetTwoStep(member.uid);
              return { text: `Two-step verification removed from ${member.email}. They can set it up again on the Account page.` };
            });
          }}
        >
          Reset two-step
        </button>
        {!isSelf && (
          <>
            <button
              type="button"
              className="btn btn--small"
              disabled={busy}
              onClick={() => {
                const disable = !member.disabled;
                if (disable && !window.confirm(`Disable ${member.email}? They are signed out within an hour and cannot sign in again.`)) return;
                void run(async () => {
                  await updateStaff(member.uid, { disabled: disable });
                  return { text: disable ? `Disabled ${member.email}.` : `Enabled ${member.email}.` };
                });
              }}
            >
              {member.disabled ? 'Enable account' : 'Disable account'}
            </button>
            <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={onDelete}>
              Delete account
            </button>
          </>
        )}
      </div>
    </form>
  );
}
